import React, { useState } from 'react';
import { Terminal, Download, ShieldCheck, Database, HardDrive, Tags, Rocket } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { maintenanceAPI } from '../../services/api';

const TASKS = [
  {
    task: 'env-check',
    mode: 'run',
    icon: ShieldCheck,
    title: '1. Verifica variabili d\'ambiente',
    description: 'Controlla che JWT_SECRET_KEY e WB_IDENTITY_KEY siano impostate e che EMERGENCY_RESET_SECRET NON lo sia. I valori non vengono mai mostrati.',
    variant: 'outline',
  },
  {
    task: 'seed-compliance',
    mode: 'force',
    icon: Database,
    title: '2. Pubblica i testi legali (--force)',
    description: 'Sovrascrive privacy, DPO, privacy candidati, whistleblowing, whistleblowing-info, codice etico e modello 231. Prima di scrivere crea automaticamente un backup ripristinabile. gender-equality non viene toccata.',
    variant: 'default',
    confirm: 'Sovrascrivo i testi legali con quelli versionati nel codice. Viene creato un backup automatico. Procedo?',
  },
  {
    task: 'migrate-categories',
    mode: 'dry-run',
    icon: Tags,
    title: '3a. Categorie whistleblowing (simulazione)',
    description: 'Mostra quali segnalazioni storiche verrebbero riallineate alle categorie ISO 37001. Non modifica nulla.',
    variant: 'outline',
  },
  {
    task: 'migrate-categories',
    mode: 'apply',
    icon: Tags,
    title: '3b. Categorie whistleblowing (esecuzione)',
    description: 'Applica la mappatura mantenendo il valore originale in report_type_legacy.',
    variant: 'secondary',
    confirm: 'Applico la mappatura delle categorie storiche. Procedo?',
  },
  {
    task: 'migrate-private-files',
    mode: 'dry-run',
    icon: HardDrive,
    title: '4a. File sensibili (simulazione)',
    description: 'Elenca i CV e gli allegati che verrebbero spostati sul prefisso privato. Non modifica nulla.',
    variant: 'outline',
  },
  {
    task: 'migrate-private-files',
    mode: 'apply',
    icon: HardDrive,
    title: '4b. File sensibili (esecuzione)',
    description: 'Copia su private/, verifica dimensione ed ETag, cancella l\'oggetto pubblico e aggiorna i riferimenti nel database.',
    variant: 'destructive',
    confirm: 'Sposto definitivamente i file sensibili sul prefisso privato e aggiorno il database. Eseguire solo dopo aver controllato la simulazione. Procedo?',
  },
  {
    task: 'verify-private-files',
    mode: 'run',
    icon: ShieldCheck,
    title: '5. Verifica finale accessi anonimi',
    description: 'Per ogni CV e allegato tenta un download anonimo: deve rispondere 403. Segnala ogni file ancora pubblico.',
    variant: 'outline',
  },
];

export const MaintenanceCard = () => {
  const [running, setRunning] = useState(null);
  const [result, setResult] = useState(null);

  const runTask = async ({ task, mode, confirm: confirmMessage }) => {
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    setRunning(`${task}:${mode}`);
    setResult(null);
    try {
      const res = await maintenanceAPI.run(task, mode);
      setResult(res.data);
      if (res.data.exit_code === 0) {
        toast.success(`${task} completato`);
      } else {
        toast.error(`${task} terminato con codice ${res.data.exit_code}: controlla l'output`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Errore durante l'esecuzione di ${task}`);
    } finally {
      setRunning(null);
    }
  };

  const downloadBackup = async () => {
    try {
      const res = await maintenanceAPI.complianceBackup();
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `compliance-pages-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Backup di ${res.data.count} pagine scaricato`);
    } catch (error) {
      toast.error('Errore nel download del backup');
    }
  };

  return (
    <Card className="glass-card" data-testid="maintenance-card">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Rocket className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">Messa in produzione</CardTitle>
              <CardDescription>
                Esegui le operazioni di go-live nell'ordine indicato. Ogni esecuzione viene registrata.
              </CardDescription>
            </div>
          </div>
          <Button variant="outline" onClick={downloadBackup} data-testid="download-compliance-backup-btn">
            <Download className="w-4 h-4 mr-2" />
            Scarica backup testi legali
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {TASKS.map((item) => {
          const Icon = item.icon;
          const key = `${item.task}:${item.mode}`;
          return (
            <div
              key={key}
              className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-lg bg-muted/40"
              data-testid={`maintenance-task-${item.task}-${item.mode}`}
            >
              <div className="flex items-start gap-3 min-w-0">
                <Icon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{item.title}</p>
                  <p className="text-xs text-muted-foreground">{item.description}</p>
                </div>
              </div>
              <Button
                variant={item.variant}
                size="sm"
                onClick={() => runTask(item)}
                disabled={running !== null}
                data-testid={`run-${item.task}-${item.mode}-btn`}
              >
                {running === key ? 'Esecuzione...' : 'Esegui'}
              </Button>
            </div>
          );
        })}

        {result && (
          <div className="space-y-2 pt-2" data-testid="maintenance-output">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <Badge variant={result.exit_code === 0 ? 'outline' : 'destructive'}>
                {result.task} ({result.mode}) · exit {result.exit_code}
              </Badge>
              <span className="text-muted-foreground text-xs">
                {result.duration_seconds}s
                {result.compliance_backup_id ? ` · backup: ${result.compliance_backup_id}` : ''}
              </span>
            </div>
            <pre className="p-4 rounded-lg bg-background border border-border text-xs overflow-x-auto max-h-96 whitespace-pre-wrap">
              <Terminal className="w-3 h-3 inline mr-1" />
              {result.output || '(nessun output)'}
            </pre>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MaintenanceCard;
